import { store } from '@wordpress/interactivity';
import { getWooCommercePlaceholderImage, decodeHTMLEntitiesDeep } from '../../core/shared/dom-utils.js';
import { convertStoreApiItemToCaddyFormat, convertCartItems } from '../../core/shared/converters.js';
import { formatPrice, convertStoreApiPrice } from '../../core/shared/formatters.js';

let slideMutationObserver = null;

function escapeHtml(value) {
	return String(value ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

function escapeAttr(value) {
	return escapeHtml(value);
}

function sanitizeUrl(value) {
	if (!value) {
		return '';
	}
	try {
		const url = new URL(value, window.location.origin);
		if (url.protocol === 'http:' || url.protocol === 'https:') {
			return url.href;
		}
	} catch (e) {
		// Ignore URL parse errors and return safe fallback.
	}
	return '';
}

function renderMessage(container, message) {
	const safeMessage = escapeHtml(message || '');
	container.innerHTML = `<p>${safeMessage}</p>`;
}

function getRecommendationLabel(state, key, fallback) {
	if (state?.i18n?.[key]) {
		return state.i18n[key];
	}

	const container = document.querySelector('.cc-cart-container[data-label-add-to-cart]');
	if (!container?.dataset) {
		return fallback;
	}

	switch (key) {
		case 'addToCart':
			return container.dataset.labelAddToCart || fallback;
		case 'seeOptions':
			return container.dataset.labelSeeOptions || fallback;
		case 'viewProducts':
			return container.dataset.labelViewProducts || fallback;
		default:
			return fallback;
	}
}

function ensureRecommendationI18n(state) {
	if (!state) {
		return;
	}

	if (!state.i18n) {
		state.i18n = {};
	}

	state.i18n.addToCart = state.i18n.addToCart || getRecommendationLabel(state, 'addToCart', 'Add to cart');
	state.i18n.seeOptions = state.i18n.seeOptions || getRecommendationLabel(state, 'seeOptions', 'Select options');
	state.i18n.viewProducts = state.i18n.viewProducts || getRecommendationLabel(state, 'viewProducts', 'View products');
}

/**
 * Initialize recommendations module
 *
 * @param {Object} coreFunctions - Core functions from view.js
 * @returns {Object} - Public API with initializeRecommendations function
 */
export function initializeRecommendationsModule(coreFunctions) {
	const { updateCartTotals, updateAppliedCouponsDisplay } = coreFunctions;

	// Cache for tracking what recommendations are currently loaded
	let lastLoadedProductId = null;
	let lastCartProductIds = [];
	let usedInitialRecommendations = false;

	/**
	 * Initialize recommendations functionality
	 */
	function initializeRecommendations() {
		const recommendationsContainer = document.getElementById('cc-store-api-recommendations');
		if (!recommendationsContainer) {
			return;
		}

		// Check if recommendations are enabled
		// Allow if enabled or empty (default enabled), only skip if explicitly disabled
		const isEnabled = recommendationsContainer.dataset.enabled;
		if (isEnabled === 'disabled') {
			return;
		}

		// Get product ID from cart state instead of static data attribute
		const { state } = store('caddy/cart');
		ensureRecommendationI18n(state);

		// Check if we have pre-loaded recommendations from server and haven't used them yet
		if (!usedInitialRecommendations && state.initialRecommendations && state.initialRecommendations.length > 0) {
			usedInitialRecommendations = true;
			// Use pre-loaded recommendations immediately (no API fetch needed)
			renderRecommendations(state.initialRecommendations, recommendationsContainer);
			// Update cache
			if (state.items && state.items.length > 0) {
				const topLevel = state.items.filter(item => !item.isBundledItem);
				const lastProduct = topLevel.length > 0 ? topLevel[topLevel.length - 1] : state.items[state.items.length - 1];
				lastLoadedProductId = lastProduct.productId;
				lastCartProductIds = [...state.items.map(item => item.productId)];
			} else {
				lastLoadedProductId = 0;
				lastCartProductIds = [];
			}
			return;
		}

		if (!state.items || state.items.length === 0) {
			// Load best sellers when cart is empty
			// This ensures recommendations always load even if state isn't ready yet
			if (lastLoadedProductId !== 0) {
				lastLoadedProductId = 0;
				lastCartProductIds = [];
				loadRecommendations(0, recommendationsContainer);
			}
			return;
		}

		// Use the LAST top-level product in the cart for recommendations (skip bundled children)
		const topLevelItems = state.items.filter(item => !item.isBundledItem);
		const lastProduct = topLevelItems.length > 0 ? topLevelItems[topLevelItems.length - 1] : state.items[state.items.length - 1];
		const productId = lastProduct.productId;

		// Get all cart product IDs to exclude from recommendations
		const cartProductIds = state.items.map(item => item.productId);

		// Check if we need to reload - only reload if the product ID changed or cart composition changed
		// Use spread to avoid mutating original arrays with sort()
		const cartProductIdsString = [...cartProductIds].sort().join(',');
		const lastCartProductIdsString = [...lastCartProductIds].sort().join(',');

		if (productId === lastLoadedProductId && cartProductIdsString === lastCartProductIdsString) {
			// Same product and same cart composition - skip reload
			return;
		}

		// Update cache with a copy of the array
		lastLoadedProductId = productId;
		lastCartProductIds = [...cartProductIds];

		// If productId is invalid (0 or null), fall back to popular products
		if (!productId || productId === 0) {
			loadRecommendations(null, recommendationsContainer);
			return;
		}

		loadRecommendations(productId, recommendationsContainer, cartProductIds);
	}

	/**
	 * Render recommendations from pre-loaded data (no API fetch)
	 */
	function renderRecommendations(products, container) {
		if (!products || products.length === 0) {
			const { state } = store('caddy/cart');
			renderMessage(container, state.i18n?.recommendationsEmpty || 'No recommendations available');
			return;
		}

		// Create slide containers
		const emptySlides = products.map((_, index) => {
			return `<div class="cc-slide" data-product-index="${index}"></div>`;
		}).join('');

		container.innerHTML = emptySlides;

		// Initialize slider functionality
		initializeRecommendationsSlider();

		// Store products data for lazy loading
		container.dataset.productsData = JSON.stringify(products);

		// Populate all slides immediately since we have the data
		products.forEach((product, index) => {
			populateSlide(index, product, container);
		});

		// Initialize button handlers
		initializeRecommendationButtons();
	}

	/**
	 * Load product recommendations with lazy loading for faster initial display
	 */
	async function loadRecommendations(productId, container, cartProductIds = []) {
		try {
			// Show skeleton immediately while loading
			container.innerHTML = `<div class="cc-slide" data-product-index="0" style="width: 400px;">
				<div class="up-sells-product" style="width: 400px;">
					<div class="cc-up-sells-image">
						<div style="width: 95px; height: 95px; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: cc-skeleton-loading 1.5s infinite; border-radius: 4px;"></div>
					</div>
					<div class="cc-up-sells-details">
						<div style="height: 18px; width: 160px; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: cc-skeleton-loading 1.5s infinite; border-radius: 4px; margin-bottom: 12px;"></div>
						<div style="height: 16px; width: 90px; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: cc-skeleton-loading 1.5s infinite; border-radius: 4px; margin-bottom: 15px;"></div>
						<div style="height: 38px; width: 120px; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: cc-skeleton-loading 1.5s infinite; border-radius: 4px;"></div>
					</div>
				</div>
			</div>`;

			// Get product ID from container data attribute if not provided
			if (!productId && container) {
				productId = container.dataset.productId || container.getAttribute('data-product-id');
				// Convert to number and validate
				productId = parseInt(productId);
				if (isNaN(productId) || productId === 0) {
					productId = null;
				}
			}

			// Build the API URL using our custom endpoint that respects settings
			let apiUrl;
			if (productId) {
				apiUrl = `${window.location.origin}/wp-json/caddy/v1/recommendations/${productId}?limit=3`;
				// Add cart product IDs as query parameter to exclude them
				if (cartProductIds.length > 0) {
					apiUrl += `&exclude=${cartProductIds.join(',')}`;
				}
			} else {
				// Fallback to WooCommerce Store API for best sellers if no product ID
				apiUrl = `${window.location.origin}/wp-json/wc/store/v1/products?per_page=3&orderby=popularity&_fields=id,name,permalink,prices,images,type`;
			}

			// Fetch recommendations from our endpoint
			const response = await fetch(apiUrl, {
				credentials: 'same-origin',
				headers: {
					'Content-Type': 'application/json'
				}
			});

			if (!response.ok) {
				const { state } = store('caddy/cart');
				throw new Error(state.i18n.recommendationsLoadError);
			}

			const data = await response.json();

			// Handle both our custom endpoint format and WooCommerce Store API format
			const products = data.products || data;

			if (!products || products.length === 0) {
				const { state } = store('caddy/cart');
				renderMessage(container, state.i18n.recommendationsEmpty);
				return;
			}

			// Create slide containers - only show skeleton for first slide to prevent flash
			const emptySlides = products.map((_, index) => {
				if (index === 0) {
					// First slide gets skeleton content matching the up-sells-product structure
					return `<div class="cc-slide" data-product-index="${index}" style="width: 400px;">
						<div class="up-sells-product" style="width: 400px;">
							<div class="cc-up-sells-image">
								<div style="width: 95px; height: 95px; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: cc-skeleton-loading 1.5s infinite; border-radius: 4px;"></div>
							</div>
							<div class="cc-up-sells-details">
								<div style="height: 18px; width: 160px; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: cc-skeleton-loading 1.5s infinite; border-radius: 4px; margin-bottom: 12px;"></div>
								<div style="height: 16px; width: 90px; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: cc-skeleton-loading 1.5s infinite; border-radius: 4px; margin-bottom: 15px;"></div>
								<div style="height: 38px; width: 120px; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%); background-size: 200% 100%; animation: cc-skeleton-loading 1.5s infinite; border-radius: 4px;"></div>
							</div>
						</div>
					</div>`;
				} else {
					// Other slides are empty containers for slider navigation
					return `<div class="cc-slide" data-product-index="${index}"></div>`;
				}
			}).join('');

			container.innerHTML = emptySlides;

			// Initialize slider functionality IMMEDIATELY to prevent vertical stacking
			initializeRecommendationsSlider();

			// Store products data for lazy loading
			container.dataset.productsData = JSON.stringify(products);

			// Load the first product with a small delay to ensure slider CSS is applied
			setTimeout(() => {
				populateSlide(0, products[0], container);
				// Set up intersection observer or slider navigation events to load other slides on demand
				setupLazySlideLoading(products, container);
			}, 10);

		} catch (error) {
			const { state } = store('caddy/cart');
			renderMessage(container, state.i18n.recommendationsLoadError);
		}
	}

	/**
	 * Populate a specific slide with product data
	 */
	function populateSlide(slideIndex, product, container) {
		const slide = container.querySelector(`[data-product-index="${slideIndex}"]`);
		if (!slide || slide.dataset.populated === 'true') {
			return; // Already populated or slide doesn't exist
		}

		const salePrice = product.prices?.sale_price ? convertStoreApiPrice(product.prices.sale_price) : null;
		const regularPrice = product.prices?.regular_price ? convertStoreApiPrice(product.prices.regular_price) : null;
		const isOnSale = salePrice && salePrice < regularPrice;

		let priceHTML = '';
		if (isOnSale && regularPrice && salePrice) {
			priceHTML = `
				<del><span class="woocommerce-Price-amount amount">${escapeHtml(formatPrice(regularPrice))}</span></del>
				<span class="woocommerce-Price-amount amount">${escapeHtml(formatPrice(salePrice))}</span>
			`;
		} else if (regularPrice) {
			priceHTML = `<span class="woocommerce-Price-amount amount">${escapeHtml(formatPrice(regularPrice))}</span>`;
		}

		// Use thumbnail URL from Store API if available, otherwise fall back to src
		const imageUrl = product.images && product.images[0]
			? (product.images[0].thumbnail || product.images[0].src)
			: null;
		const safeImageUrl = sanitizeUrl(imageUrl) || sanitizeUrl(getWooCommercePlaceholderImage());
		const decodedProductName = decodeHTMLEntitiesDeep(product.name || '');
		const safeProductName = escapeHtml(decodedProductName);
		const safePermalink = sanitizeUrl(product.permalink) || '#';
		const safeProductId = Number.parseInt(product.id, 10) || 0;
		const imageHTML = imageUrl
			? `<img src="${escapeAttr(safeImageUrl)}" alt="${safeProductName}" loading="lazy" class="attachment-woocommerce_thumbnail" />`
			: `<img src="${escapeAttr(safeImageUrl)}" alt="${safeProductName}" loading="lazy" class="attachment-woocommerce_thumbnail wc-placeholder" />`;

		// Determine button type based on product type
		const isVariableProduct = product.type === 'variable';
		const isGroupedProduct = product.type === 'grouped';

		// Get translated strings from state
		const { state } = store('caddy/cart');
		const i18n = state.i18n || {};

		let buttonHTML = '';
		if (isVariableProduct) {
			buttonHTML = `<a href="${escapeAttr(safePermalink)}" class="button product_type_variable">${escapeHtml(getRecommendationLabel(state, 'seeOptions', 'Select options'))}</a>`;
		} else if (isGroupedProduct) {
			buttonHTML = `<a href="${escapeAttr(safePermalink)}" class="button product_type_grouped">${escapeHtml(getRecommendationLabel(state, 'viewProducts', 'View products'))}</a>`;
		} else {
			buttonHTML = `<a href="?add-to-cart=${safeProductId}" class="button product_type_simple add_to_cart_button" data-product_id="${safeProductId}" data-quantity="1">${escapeHtml(getRecommendationLabel(state, 'addToCart', 'Add to cart'))}</a>`;
		}

		const productHTML = `
				<div class="up-sells-product">
					<div class="cc-up-sells-image">
						<a href="${escapeAttr(safePermalink)}">${imageHTML}</a>
					</div>
					<div class="cc-up-sells-details">
						<a href="${escapeAttr(safePermalink)}" class="title">${safeProductName}</a>
						<div class="cc_item_total_price">
							<span class="price">${priceHTML}</span>
						</div>
					${buttonHTML}
				</div>
			</div>
		`;

		slide.innerHTML = productHTML;
		slide.dataset.populated = 'true';
	}

	/**
	 * Set up lazy loading for slides when they become visible or are navigated to
	 */
	function setupLazySlideLoading(products, container) {
		if (slideMutationObserver) {
			slideMutationObserver.disconnect();
			slideMutationObserver = null;
		}

		// Track which slides we need to load
		const slidesToLoad = new Set();

		// Listen for slider navigation to load slides on demand
		const loadSlideOnDemand = (slideIndex) => {
			if (slideIndex > 0 && !slidesToLoad.has(slideIndex) && products[slideIndex]) {
				slidesToLoad.add(slideIndex);
				populateSlide(slideIndex, products[slideIndex], container);
				// Re-initialize button handlers for newly populated slides
				initializeRecommendationButtons();
			}
		};

		// Monitor slider transformations to detect navigation
		const sliderWrapper = container.parentElement;
			if (sliderWrapper) {
				// Mutation observer to watch for transform changes
				slideMutationObserver = new MutationObserver(() => {
					const transform = container.style.transform;
					if (transform) {
					// Extract translateX percentage
					const match = transform.match(/translateX\(([-\d.]+)%\)/);
					if (match) {
						const translateX = parseFloat(match[1]);
						const slideWidth = 100 / products.length; // Each slide is this % wide
						const currentSlide = Math.round(Math.abs(translateX) / slideWidth);

						// Load current slide and next slide
						loadSlideOnDemand(currentSlide);
						loadSlideOnDemand(currentSlide + 1);
					}
				}
			});

				slideMutationObserver.observe(container, {
					attributes: true,
					attributeFilter: ['style']
				});
			}

		// Initialize button handlers immediately (for first slide)
		initializeRecommendationButtons();
	}

	/**
	 * Initialize recommendations slider functionality
	 */
	function initializeRecommendationsSlider() {
		const container = document.querySelector('.cc-pl-recommendations');
		const slides = container?.querySelectorAll('.cc-slide');
		const prevBtn = document.querySelector('.caddy-prev');
		const nextBtn = document.querySelector('.caddy-next');

		if (!container || !slides || slides.length === 0) {
			return;
		}

		// Remove any existing event listeners by cloning and replacing the buttons
		if (prevBtn) {
			const newPrevBtn = prevBtn.cloneNode(true);
			prevBtn.parentNode.replaceChild(newPrevBtn, prevBtn);
		}
		if (nextBtn) {
			const newNextBtn = nextBtn.cloneNode(true);
			nextBtn.parentNode.replaceChild(newNextBtn, nextBtn);
		}

		// Re-query the buttons after cloning
		const prevButton = document.querySelector('.caddy-prev');
		const nextButton = document.querySelector('.caddy-next');

		let currentSlide = 0;
		const totalSlides = slides.length;

		// Set up slider wrapper (parent of container)
		const sliderWrapper = container.parentElement;
		if (sliderWrapper) {
			sliderWrapper.style.overflow = 'hidden';
			sliderWrapper.style.position = 'relative';
		}

		// Set up slider container
		container.style.display = 'flex';
		container.style.transition = 'transform 0.3s ease';
		container.style.width = `${totalSlides * 100}%`;

		// Set up individual slides
		slides.forEach((slide) => {
			slide.style.flex = '0 0 auto';
			slide.style.width = `${100 / totalSlides}%`;
			slide.style.paddingRight = '10px';
			slide.style.boxSizing = 'border-box';
		});

		function updateSlider() {
			const translateX = -(currentSlide * (100 / totalSlides));
			container.style.transform = `translateX(${translateX}%)`;

			// Update button states - disable when at edges
			if (prevButton) {
				prevButton.style.opacity = currentSlide > 0 ? '1' : '0.1';
				prevButton.style.pointerEvents = currentSlide > 0 ? 'auto' : 'none';
			}
			if (nextButton) {
				nextButton.style.opacity = currentSlide < totalSlides - 1 ? '1' : '0.1';
				nextButton.style.pointerEvents = currentSlide < totalSlides - 1 ? 'auto' : 'none';
			}
		}

		// Style navigation buttons to prevent text selection
		[nextButton, prevButton].forEach(btn => {
			if (btn) {
				btn.style.userSelect = 'none';
				btn.style.webkitUserSelect = 'none';
				btn.style.cursor = 'pointer';
				btn.style.outline = 'none';
			}
		});

		// Add event listeners to the new buttons
		if (nextButton) {
			nextButton.addEventListener('click', (e) => {
				e.preventDefault();
				e.stopPropagation();
				if (currentSlide < totalSlides - 1) {
					currentSlide++;
					updateSlider();
				}
			});
		}

		if (prevButton) {
			prevButton.addEventListener('click', (e) => {
				e.preventDefault();
				e.stopPropagation();
				if (currentSlide > 0) {
					currentSlide--;
					updateSlider();
				}
			});
		}

		// Initial state
		updateSlider();
	}

	/**
	 * Initialize add-to-cart buttons within recommendations
	 */
	function initializeRecommendationButtons() {
		// Find all add-to-cart buttons within the recommendations container
		const recContainer = document.querySelector('.cc-pl-recommendations');
		if (!recContainer) {
			return;
		}

		// Only intercept "Add to Cart" buttons (simple products), not "See Options" buttons (variable products)
		const atcButtons = recContainer.querySelectorAll('a.add_to_cart_button');

		// Handle simple product "Add to Cart" buttons
		atcButtons.forEach((button) => {
			// Skip buttons that already have our listener attached
			if (button.dataset.caddyRecHandled === 'true') {
				return;
			}
			button.dataset.caddyRecHandled = 'true';

			// Add event listener to intercept and use Store API
			button.addEventListener('click', async (event) => {
				// Skip variable and grouped product buttons - let them navigate to product page
				if (button.classList.contains('product_type_variable') ||
					button.classList.contains('product_type_grouped')) {
					return; // Don't prevent default, allow normal navigation
				}

				// Skip "Read more" buttons (non-purchasable products)
				if (!button.classList.contains('add_to_cart_button')) {
					return; // Don't prevent default, allow normal navigation
				}

				event.preventDefault();
				event.stopPropagation();

				const productId = button.dataset.product_id || button.getAttribute('data-product_id');
				const quantity = button.dataset.quantity || 1;

				if (!productId) {
					return;
				}

				// Add loading state to button
				const originalText = button.textContent;
				const { state } = store('caddy/cart');
				button.textContent = state.i18n.adding;
				button.classList.add('loading');

				try {
					// Use WooCommerce Store API
					const addToCartData = {
						id: parseInt(productId),
						quantity: parseInt(quantity)
					};

					const storeApiUrl = `${window.location.origin}/wp-json/wc/store/v1/cart/add-item`;

					// Get Store API nonce from meta tag
					let storeApiNonce = null;
					const storeApiNonceMeta = document.querySelector('meta[name="wc-store-api-nonce"]');
					if (storeApiNonceMeta) {
						storeApiNonce = storeApiNonceMeta.getAttribute('content');
					}

					const headers = {
						'Content-Type': 'application/json',
					};

					if (storeApiNonce) {
						headers['Nonce'] = storeApiNonce;
					}

					// Make the Store API request
					const response = await fetch(storeApiUrl, {
						method: 'POST',
						credentials: 'same-origin',
						headers: headers,
						body: JSON.stringify(addToCartData)
					});

					if (response.ok) {
						// Store API add-item returns full cart - use it directly!
						const cartData = await response.json();
						const cartStore = store('caddy/cart');

						// Convert all items from Store API to Caddy format
						const caddyItems = convertCartItems(cartData.items);

						// Update entire cart state from response
						cartStore.state.items = caddyItems;
						cartStore.state.cartCount = cartData.items_count;
						cartStore.state.isItemSingular = cartData.items_count === 1;
						updateCartTotals(cartStore.state, cartData);
						cartStore.state.coupons = cartData.coupons || [];
						cartStore.state.discountTotal = cartData.totals.total_discount ? convertStoreApiPrice(cartData.totals.total_discount) : 0;

						// Update applied coupons display
						updateAppliedCouponsDisplay(cartData.coupons || []);

						// Trigger WooCommerce add to cart event
						document.dispatchEvent(new CustomEvent('wc_add_to_cart', {
							detail: { productId, quantity }
						}));

						// Refresh recommendations based on newly added product
						initializeRecommendations();

						// Auto-open cart after successful add (skip fetch - we already have fresh data!)
						const { state: cartOpenState } = store('caddy/cart');
						if (!cartOpenState.isOpen) {
							setTimeout(() => {
								store('caddy/cart').actions.openCart('cart', true);
							}, 100);
						}

						// Update button text to show success
						const { state: cartState } = store('caddy/cart');
						button.textContent = cartState.i18n.addedCheckmark;
						setTimeout(() => {
							button.textContent = originalText;
						}, 1500);

					} else {
						// Handle errors
						const { state: errorState } = store('caddy/cart');
						button.textContent = errorState.i18n.error || 'Error';
						button.classList.remove('loading');
						setTimeout(() => {
							button.textContent = originalText;
						}, 2000);
					}

				} catch (error) {
					// Error adding to cart from recommendations
					const { state: errorState } = store('caddy/cart');
					button.textContent = errorState.i18n.error || 'Error';
					button.classList.remove('loading');
					setTimeout(() => {
						button.textContent = originalText;
					}, 2000);
				}
			});
		});
	}

	// Return public API
	return {
		initializeRecommendations
	};
}
